import { TAU, WHITE, circle, glow, polygon } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 1.5;
export const NAVY = 0x1b2140;
export const OUT = 0x040612;
const SKIN = '#d59d6c';

const HAT = '#1b2140';
const CROWN = '#262e5a';
const BRIM_SHINE = 'rgba(143,155,208,.35)';
const LAPEL = 'rgba(27,33,64,.55)';
const RIM = 'rgba(4,6,18,.9)';

export const HAT_AT = 2;
export const HANDS = { along: 9, across: 14, swing: 1.2 } as const;
// How far his tails turn as they swing, in radians.
export const TAIL_SWAY = 0.07;
// Down the back of his tailcoat, one per bad vibe the charged shot will cross.
export const BUTTONS = Array.from({ length: 5 }, (_, index) => {
  const across = -8 + index * 4;
  return { along: -12.6 + Math.abs(across) * 0.28, across };
});

// Every body piece shares one frame, centred on him and facing +x, so they all sit at his place.
const BODY: readonly [number, number] = [84, 68];

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function body(draw: (ctx: Ctx) => void): Piece {
  return [BODY[0], BODY[1], REFERENCE_RADIUS, units(draw)];
}

function fillRim(ctx: Ctx, fill: string, width: number): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = RIM;
  ctx.stroke();
}

function tailPath(ctx: Ctx, side: number): void {
  polygon(ctx, [
    [-5, side * 4],
    [-22, side * 8.5],
    [-11, side * 11.5],
  ]);
}

function backPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.moveTo(-4, -10);
  ctx.quadraticCurveTo(-16, -10, -16.5, 0);
  ctx.quadraticCurveTo(-16, 10, -4, 10);
  ctx.closePath();
}

function tail(side: number): (ctx: Ctx) => void {
  return (ctx) => {
    tailPath(ctx, side);
    fillRim(ctx, WHITE, 1.3);
    ctx.beginPath();
    ctx.moveTo(-5, side * 4);
    ctx.lineTo(-18, side * 7.6);
    ctx.strokeStyle = LAPEL;
    ctx.lineWidth = 1;
    ctx.stroke();
  };
}

function back(ctx: Ctx): void {
  backPath(ctx);
  fillRim(ctx, HAT, 1.3);
}

function torso(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, 9, 14.5, 0, 0, TAU);
  fillRim(ctx, '#d4d4d4', 2);
  ctx.beginPath();
  ctx.ellipse(1.5, 0, 4.5, 9.6, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(4, side * 5.5);
    ctx.lineTo(-2, side);
    ctx.strokeStyle = LAPEL;
    ctx.lineWidth = 1.2;
    ctx.lineCap = 'round';
    ctx.stroke();
  }
}

function silhouette(ctx: Ctx): void {
  ctx.fillStyle = WHITE;
  for (const side of [-1, 1]) {
    tailPath(ctx, side);
    ctx.fill();
  }
  backPath(ctx);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(0, 0, 9, 14.5, 0, 0, TAU);
  ctx.fill();
  circle(ctx, 10.4, HAT_AT, 0);
  ctx.fill();
}

function brim(ctx: Ctx): void {
  circle(ctx, 10.4, HAT_AT, 0);
  fillRim(ctx, HAT, 1.5);
  ctx.beginPath();
  ctx.arc(HAT_AT, 0, 9, 3.5, 4.6);
  ctx.strokeStyle = BRIM_SHINE;
  ctx.lineWidth = 1;
  ctx.stroke();
}

function ribbon(ctx: Ctx): void {
  circle(ctx, 7.4, HAT_AT, 0);
  fillRim(ctx, WHITE, 1);
}

function crown(ctx: Ctx): void {
  circle(ctx, 6.1, HAT_AT, 0);
  fillRim(ctx, CROWN, 1.1);
  ctx.beginPath();
  ctx.arc(HAT_AT, 0, 3.6, 3.6, 4.7);
  ctx.strokeStyle = 'rgba(255,255,255,.45)';
  ctx.lineWidth = 1.3;
  ctx.lineCap = 'round';
  ctx.stroke();
}

function hand(ctx: Ctx): void {
  circle(ctx, 3.2);
  fillRim(ctx, SKIN, 1.3);
}

function button(ctx: Ctx): void {
  circle(ctx, 1.9);
  fillRim(ctx, WHITE, 0.7);
}

function shine(ctx: Ctx): void {
  glow(ctx, WHITE, 3);
  circle(ctx, 3.4);
  ctx.fillStyle = WHITE;
  ctx.fill();
}

function nothing(): void {
  // His shadow is the long one his family lays on the ground, never the sun's.
}

export const ECHASSIER_PIECES = {
  tailLeft: body(tail(-1)),
  tailRight: body(tail(1)),
  back: body(back),
  torso: body(torso),
  silhouette: body(silhouette),
  brim: body(brim),
  ribbon: body(ribbon),
  crown: body(crown),
  hand: [12, 12, REFERENCE_RADIUS, units(hand)],
  button: [8, 8, REFERENCE_RADIUS, units(button)],
  shine: [20, 20, REFERENCE_RADIUS, units(shine)],
  shadow: [4, 4, REFERENCE_RADIUS, nothing],
} satisfies Record<string, Piece>;
